import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Stack,
  Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

interface AddAgentProp {
	open: boolean;
	onClose: () => void;
	onSubmit: (agents: NewAgentRequest[]) => void;
}

export interface NewAgentRequest {
	agent_id: string,
	agent_username: string,
	agent_password: string,
	is_active: boolean
}

const AddAgentModal: React.FC<AddAgentProp> = ({ open, onClose, onSubmit }) => {
  // Initial state with one empty agent row
  const initialRow: NewAgentRequest = { agent_id: '', agent_username: '', agent_password: '', is_active: true };
  const [agents, setAgents] = useState<NewAgentRequest[]>([initialRow]);

  // Handle input changes for a specific row
  const handleInputChange = (index: number, field: string, value: string) => {
    const newAgents = [...agents];
    newAgents[index] = { ...newAgents[index], [field]: value };
    setAgents(newAgents);
  };

  // Add a new empty row to the form
  const addRow = () => {
    setAgents([...agents, { agent_id: '', agent_username: '', agent_password: '', is_active: true }]);
  };

  // Remove a specific row
  const removeRow = (index: number) => {
    const newAgents = agents.filter((_, i) => i !== index);
    setAgents(newAgents);
  };

  const handleSubmit = () => {
    // Basic validation: Filter out completely empty rows
    const validAgents = agents.filter(a => a.agent_id && a.agent_username && a.agent_password);
    onSubmit(validAgents);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Add New Agents</DialogTitle>
      
      <DialogContent dividers>
        <Stack spacing={3}>
          {agents.map((agent, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <TextField
                label="Agent ID"
                variant="outlined"
                size="small"
                value={agent.agent_id}
                onChange={(e) => handleInputChange(index, 'agent_id', e.target.value)}
              />
              <TextField
                label="Username"
                variant="outlined"
                size="small"
                value={agent.agent_username}
                onChange={(e) => handleInputChange(index, 'agent_username', e.target.value)}
              />
              <TextField
                label="Password"
                type="password"
                variant="outlined"
                size="small"
                value={agent.agent_password}
                onChange={(e) => handleInputChange(index, 'agent_password', e.target.value)}
              />
              
              {/* Only show delete if there's more than one row */}
              {agents.length > 1 && (
                <IconButton color="error" onClick={() => removeRow(index)} sx={{ mt: 0.5 }}>
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))}
        </Stack>

        <Button 
          startIcon={<AddIcon />} 
          onClick={addRow} 
          sx={{ mt: 3 }}
          variant="text"
        >
          Add Another Agent
        </Button>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} color="inherit">Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Submit {agents.length} Agent{agents.length > 1 ? 's' : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddAgentModal;